import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { BlockedPhoneNumberFilterOptions } from '../models/blocked-phone-number-filter-option';
import { BlockedPhoneNumberRegisterOptions } from '../models/blocked-phone-number-register-options';
import { deleteBlockedPhoneNumberMutation } from '../queries/delete-blocked-phone-number-detail.graphql';
import { DeleteBlockedPhoneNumberVariables } from '../queries/generated/DeleteBlockedPhoneNumber';
import {
   GetBlockedPhoneNumberRegister,
   GetBlockedPhoneNumberRegisterVariables,
} from '../queries/generated/GetBlockedPhoneNumberRegister';
import { getBlockedPhoneNumberQuery } from '../queries/getBlockedPhoneNumberRegister.graphql';

@Injectable({ providedIn: 'root' })
export class BlockedPhoneNumberRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getBlockedPhoneNumbers(
      endCursor: string | null,
      filters: BlockedPhoneNumberFilterOptions[] | null,
      quickFilters: BlockedPhoneNumberRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetBlockedPhoneNumberRegister>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertBlockedPhoneNumberFilterOptions.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.filterOptions.push({
               field: 'PhoneNumber',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Comment',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }
      const variables = this.getBlockedPhoneNumbersVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetBlockedPhoneNumberRegister, GetBlockedPhoneNumberRegisterVariables>({
         query: getBlockedPhoneNumberQuery,
         variables,
      });
   }

   private convertBlockedPhoneNumberFilterOptions(option: BlockedPhoneNumberFilterOptions): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: BlockedPhoneNumberFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'blockedPhoneNumber':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.blockedPhoneNumberId;
                 })
               : option.value?.blockedPhoneNumberId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
      return null;
   }

   private getBlockedPhoneNumbersVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetBlockedPhoneNumberRegisterVariables {
      const variables: GetBlockedPhoneNumberRegisterVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   deleteBlockedPhoneNumber(blockedPhoneNumberId: number): Observable<ExecutionResult<void>> {
      return this.apiService.query<void, DeleteBlockedPhoneNumberVariables>({
         query: deleteBlockedPhoneNumberMutation,
         variables: {
            blockedPhoneNumberId,
         },
      });
   }
}
